import { ICategory } from '@faire/web-api--source/indigofair/data/ICategory';
import { IImage } from '@faire/web-api--source/indigofair/data/IImage';
import { IPreviouslyOrderedBrand } from '@faire/web-api--source/indigofair/data/IPreviouslyOrderedBrand';
import { IRequestToShopBrand } from '@faire/web-api--source/indigofair/data/IRequestToShopBrand';
import { ISearchMakersWithFiltersRequest } from '@faire/web-api--source/indigofair/data/ISearchMakersWithFiltersRequest';
import { ISearchProductsRequest } from '@faire/web-api--source/indigofair/data/ISearchProductsRequest';
import { ISuggestedBrand } from '@faire/web-api--source/indigofair/data/ISuggestedBrand';
import { ISuggestedNavigation } from '@faire/web-api--source/indigofair/data/ISuggestedNavigation';
import { ISuggestedProduct } from '@faire/web-api--source/indigofair/data/ISuggestedProduct';

export namespace ISearchSuggestionResponse {
  export namespace ISearchRecommendation {
    /**
     * Return an instance of ISearchRecommendation with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (
      partial?: Partial<ISearchRecommendation>
    ): ISearchRecommendation =>
      ({
        ...(partial ?? {}),
      }) as ISearchRecommendation;
  }

  /**
   * This will be generated from SuggestedSearchTermProvider. It seems trendingSearches = 6 is no longer used.
   */
  export interface ISearchRecommendation {
    name: string | undefined;
    image: IImage | undefined;
  }

  export namespace ISearchQueryWithFilters {
    /**
     * Return an instance of ISearchQueryWithFilters with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (
      partial?: Partial<ISearchQueryWithFilters>
    ): ISearchQueryWithFilters =>
      ({
        filters: [],
        filter_display_names: [],
        ...(partial ?? {}),
      }) as ISearchQueryWithFilters;
  }

  /**
   * Search queries with their respective filters, if any.
   */
  export interface ISearchQueryWithFilters {
    query: string | undefined;
    filters: Array<string>;
    filter_display_names: Array<string>;
  }

  /**
   * Return an instance of ISearchSuggestionResponse with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (
    partial?: Partial<ISearchSuggestionResponse>
  ): ISearchSuggestionResponse =>
    ({
      suggested_search_queries: {},
      suggested_category_queries: {},
      suggested_brands: [],
      trending_searches: [],
      recently_viewed_products: [],
      top_search_results: [],
      recent_searches: [],
      suggested_search_queries_display_order: [],
      search_recommendations: [],
      suggested_apparel_categories: [],
      ordered_brands: [],
      recent_searches_with_filters: [],
      suggested_search_queries_with_filters: [],
      brands_you_may_know: [],
      suggested_navigations: [],
      suggested_natural_language_search_queries: [],
      ...(partial ?? {}),
    }) as ISearchSuggestionResponse;
}

export interface ISearchSuggestionResponse {
  /**
   * A map of suggested searches. The searches can be either query based (e.g., candle) or
   * be depending on categories, filters, etc.
   */
  suggested_search_queries: Partial<Record<string, ISearchProductsRequest>>;
  /**
   * A map of suggested searches. The searches can be either query based (e.g., candle) or
   * be depending on categories, filters, etc.
   */
  suggested_category_queries: Partial<
    Record<string, ISearchMakersWithFiltersRequest>
  >;
  /**
   * A list of suggested brands, including only the brand name and token.
   */
  suggested_brands: Array<ISuggestedBrand>;
  /**
   * Unique ID for the request. Must be supplied to /click-track endpoint
   * whenever a result from this response is clicked by the user.
   */
  request_id: string | undefined;
  /**
   * A list of trending search terms.
   */
  trending_searches: Array<string>;
  /**
   * A list of recently viewed products.
   * @deprecated
   */
  recently_viewed_products: Array<ISuggestedProduct>;
  /**
   * A list of top search results when the request query is not empty.
   * @deprecated
   */
  top_search_results: Array<ISuggestedProduct>;
  /**
   * A list of recently searched queries.
   * TODO deprecate this in favor of recentSearchesWithFilters if experiment is successful
   */
  recent_searches: Array<string>;
  /**
   * Order to use when displaying suggested searches in `suggestedSearchQueries`
   */
  suggested_search_queries_display_order: Array<string>;
  /**
   * A list of search recommendations.
   */
  search_recommendations: Array<ISearchSuggestionResponse.ISearchRecommendation>;
  /**
   * A list of suggested apparel categories
   * @deprecated
   */
  suggested_apparel_categories: Array<ICategory>;
  /**
   * @deprecated
   */
  ordered_brands: Array<IPreviouslyOrderedBrand>;
  /**
   * A list of recently searched queries with their respective filters, if any.
   * Deprecated because we did not ship this feature.
   * @deprecated
   */
  recent_searches_with_filters: Array<ISearchSuggestionResponse.ISearchQueryWithFilters>;
  /**
   * A list of suggested queries that user has searched before with their respective filters, if any.
   * @deprecated
   */
  suggested_search_queries_with_filters: Array<ISearchSuggestionResponse.ISearchQueryWithFilters>;
  /**
   * A list of suggested brands that user observes as Brands you may know in the empty search state.
   * @deprecated
   */
  brands_you_may_know: Array<ISuggestedBrand>;
  /**
   * A list of suggestion navigation that user observes while search query results matches some pre-defined terms.
   * @deprecated
   */
  suggested_navigations: Array<ISuggestedNavigation>;
  show_r2_b_banner: boolean | undefined;
  /**
   * A list of suggested natural language search phrases.
   */
  suggested_natural_language_search_queries: Array<string>;
  /**
   * RTS Brand information for search suggestions.
   */
  request_to_shop_brand: IRequestToShopBrand | undefined;
}
